// M4-B §8: crude hubs, takeaway zones, differentials, routes (rail / firm pipe / export) and the hub price the field
// sells at. OIL_PRICES 2000-2025 is Brent, so from 1 Jan 2000 production.netback asks m4FieldSpot() for the field's
// hub price instead of the Brent-like spot (marked M4-B hunk). Pure reads of state (no writes, no RNG).
import {
  hubPrice, hubSpread, wtiRatio, TAKEAWAY_ZONES, zoneUtilization, NEGATIVE_PRICE, lerpAt, M4_FROM,
} from '../../data/m4/rules.js';
import { spotPrice, basePriceForDay } from '../../economy/market.js';
import { eqDef, fieldDef, stat, yf } from '../defs.js';
import { flagOn } from '../m2util.js';
import { peek } from './marketCore.js';

export const NEG_DAY = NEGATIVE_PRICE.day;
export const isNegativeDay = (state) => state.day === NEG_DAY;

// Zones for fields that predate M4 (M4_SPEC §18.11: key on takeawayZone, not region lists).
export const EXTRA_ZONES = {
  us_inland:    { hub: 'wti_cushing' },                 // conventional Texas / Oklahoma / Appalachia / California barrels
  alberta_light:{ hub: 'wti_cushing', baseSpread: -5 }, // Edmonton light sweet (Leduc, Turner Valley), est.
  alaska_ns:    { hub: 'brent', baseSpread: -1 },        // ANS trades near Brent on the West Coast
};
const DEFAULT_ZONE_BY_REGION = {
  texas: 'us_inland', oklahoma: 'us_inland', pennsylvania: 'us_inland', ohio: 'us_inland', california: 'us_inland', louisiana: 'us_inland',
  ontario: 'us_inland', colorado: 'rockies', north_dakota: 'bakken', gulf_of_mexico: 'gulf_offshore', alberta: 'alberta_light', alaska: 'alaska_ns',
};
// Hubs quoted as a spread to WTI Cushing that printed below zero on 20 Apr 2020 (coastal LLS / Mars stayed positive).
const WTI_LINKED = new Set(['wti_cushing', 'wti_midland', 'bakken', 'wcs']);
const NEG_DISCOUNT_BY_ZONE = { permian: 'permian', bakken: 'bakken', alberta: 'wcs' };
// Where firm routes deliver (transport stats.deliversTo) and what that market pays.
export const DELIVERY = {
  gulf_coast: { hub: 'lls', name: 'Gulf Coast (Corpus Christi / St. James)' },
  east_coast: { hub: 'brent', spread: -1, name: 'East Coast refineries' },
  west_coast: { hub: 'brent', spread: -1, name: 'West Coast refineries' },
  patoka:     { hub: 'wti_cushing', spread: -0.5, name: 'Patoka, Illinois' },
  pacific:    { hub: 'brent', spread: -8, name: 'Pacific (Burnaby → Asia)' },
  black_sea:  { hub: 'brent', spread: -2, name: 'Novorossiysk (CPC Blend)' },
};
export const HUB_NAMES = { brent: 'Brent', wti_cushing: 'WTI Cushing', wti_midland: 'WTI Midland', bakken: 'Bakken (Clearbrook)', wcs: 'Western Canadian Select', lls: 'Light Louisiana Sweet', mars: 'Mars (Gulf sour)' };
export const EXPORT_FREIGHT = 2;       // US Gulf → Europe/Asia freight & lightering below Brent ($/bbl, est., M4_SPEC §8.2)
export const ALBERTA_EXEMPT_BBLD = 10000; // the 2019 curtailment order exempted producers under 10,000 bbl/d

export function fieldZoneId(fieldId) {
  const f = fieldDef(fieldId);
  if (!f) return null;
  return f.takeawayZone ?? DEFAULT_ZONE_BY_REGION[f.regionId] ?? null;
}
export const zoneDef = (zone) => (zone ? TAKEAWAY_ZONES[zone] ?? EXTRA_ZONES[zone] ?? null : null);
export const zoneHub = (zone) => zoneDef(zone)?.hub ?? 'brent';
export const isNorthAmericanHub = (hub) => !!hub && hub !== 'brent';

// The Brent-like world price today. On 20 Apr 2020 the market spot carries the WTI −$37.63 print; Brent itself
// (≈ $25) is the previous day's base curve with today's noise.
export function brentToday(state) {
  if (isNegativeDay(state)) return basePriceForDay(state.day - 1) * Math.exp(state.market?.noise ?? 0);
  return spotPrice(state);
}

// Price at a hub today (nominal $/bbl). `zone` only matters on the negative-price day (wellhead discounts).
export function hubPriceToday(state, hub, zone = null) {
  const y = yf(state);
  if (isNegativeDay(state) && WTI_LINKED.has(hub)) {
    const k = NEG_DISCOUNT_BY_ZONE[zone];
    return NEGATIVE_PRICE.settle + (k ? NEGATIVE_PRICE.rules.wellheadDiscountFromCushing[k] ?? 0 : 0);
  }
  return hubPrice(hub, brentToday(state), y);
}
export const wtiToday = (state) => hubPriceToday(state, 'wti_cushing');

// Extra discount from the player's own barrels pushing a zone above its pipeline capacity (spreadPerUtil above 0.9).
export function endogenousSpread(state, zone) {
  const z = TAKEAWAY_ZONES[zone];
  if (!z?.capacityKbd || !z.spreadPerUtil) return 0;
  const y = yf(state);
  const cap = Math.max(1, lerpAt(z.capacityKbd, y));
  const hist = zoneUtilization(zone, y);
  const mine = peek(state)?.zoneKbd?.[zone] ?? 0;
  if (!(mine > 0)) return 0;
  const withMe = hist + mine / cap;
  return -z.spreadPerUtil * Math.max(0, withMe - Math.max(0.9, hist));
}

// Zone price: hub + zone base spread (+ Eagle Ford's LLS − 3, Urals under sanctions, player-driven crunch).
export function zonePriceToday(state, zone) {
  const z = zoneDef(zone);
  const hub = z?.hub ?? 'brent';
  const y = yf(state);
  let p = hubPriceToday(state, hub, zone);
  if (isNegativeDay(state) && WTI_LINKED.has(hub)) return p; // the print is the wellhead price that day
  p += z?.baseSpread ?? 0;
  if (z?.lessSpread === 'lls_minus_3') p -= 3;
  if (z?.sanctionedSpread && y >= z.sanctionedSpread[0][0]) p += lerpAt(z.sanctionedSpread, y);
  p += endogenousSpread(state, zone);
  return p;
}

// Price at a delivery point reached by a firm route / rail.
export function deliveryPriceToday(state, dest) {
  const d = DELIVERY[dest];
  if (!d) return null;
  return hubPriceToday(state, d.hub) + (d.spread ?? 0);
}

// Export dock: the field must reach the Gulf (coastal zone, or a rail/firm route to the coast), hold dock access and
// the ban must be lifted (flag crudeExportsLegal, 18 Dec 2015).
function hasItemAt(state, fieldId, defId) {
  for (const it of Object.values(state.items)) if (it.defId === defId && it.loc?.type === 'field' && it.loc.id === fieldId) return true;
  return false;
}
export const exportsLegal = (state) => flagOn(state, 'crudeExportsLegal');

// The field's crude route today: { kind: 'zone'|'rail'|'firm'|'export', hub, price, dest } — pure.
export function fieldPriceInfo(state, fieldId, transportId = undefined) {
  const zone = fieldZoneId(fieldId);
  const z = zoneDef(zone);
  const hub = z?.hub ?? 'brent';
  const zonePrice = zonePriceToday(state, zone);
  const f = state.fields?.[fieldId];
  const tid = transportId === undefined ? f?.transportId : transportId;
  const tdef = tid ? eqDef(tid) : null;
  const ts = tdef?.stats ?? {};
  let kind = 'zone', price = zonePrice, dest = null, delivered = null;
  const na = isNorthAmericanHub(hub);
  if (na && tdef && tid === 'crude_by_rail') {
    dest = 'gulf_coast';
    delivered = zone === 'alberta' ? hubPriceToday(state, 'mars') - 5 : deliveryPriceToday(state, 'gulf_coast');
    kind = 'rail';
  } else if (na && ts.deliversTo && DELIVERY[ts.deliversTo]) {
    dest = ts.deliversTo; delivered = deliveryPriceToday(state, dest); kind = 'firm';
  }
  if (delivered != null) price = delivered; // the barrels are sold where the route delivers them
  // export: Gulf-reachable crude (coastal zone or a coastal route) with dock access, after the ban is lifted
  const coastal = hub === 'lls' || hub === 'mars' || dest === 'gulf_coast';
  let exportPrice = null;
  if (na && coastal && zone !== 'alberta' && f && exportsLegal(state) && hasItemAt(state, fieldId, 'export_dock')) {
    const tariff = stat(eqDef('export_dock'), 'tariffPerBbl', 0.8);
    exportPrice = brentToday(state) - EXPORT_FREIGHT - tariff;
    if (exportPrice > price) { price = exportPrice; kind = 'export'; }
  }
  return { fieldId, transportId: tid ?? null, zone, hub, kind, dest, price, zonePrice, deliveredPrice: delivered, exportPrice, negative: price < 0 };
}

// production.netback hook (M4-B): the per-bbl price before quality, royalty and transport. Identity before 2000.
export function m4FieldSpot(state, fieldId) {
  if (yf(state) < M4_FROM) return spotPrice(state);
  return fieldPriceInfo(state, fieldId).price;
}

// Alberta curtailment share (2019-20) for a zone, or 0.
export function zoneCurtailment(zone, y) {
  const z = TAKEAWAY_ZONES[zone];
  if (!z?.curtailment || y < z.curtailment[0][0]) return 0;
  return Math.max(0, lerpAt(z.curtailment, y));
}

// Spread of a zone's hub vs WTI Cushing ($/bbl) — for the takeaway card and the takeawayCrunch trigger.
export function zoneSpreadVsWti(state, zone) {
  return zonePriceToday(state, zone) - wtiToday(state);
}
export { hubSpread, wtiRatio };
